import { Box, Button, Checkbox } from '@mui/material';
import { UserData } from '../../../data_layer/user/UserData';
import { FunctionComponent, useState, useEffect } from 'react';
import { ErrorPageNoAdmin } from '../../error/ErrorPageNoAdmin';
import { ApiObjBaseData } from '../../../api/object/base_data/ApiObjBaseData';
import { ApiObjAdminBaseData } from '../../../api/object/admin/ApiObjAdminBaseData';
import { api_admin_user_get_nix_options } from '../../../api/endpoint/admin/user/nix_option/api_admin_user_get_nix_options';
import { api_admin_user_save_nix_options } from '../../../api/endpoint/admin/user/nix_option/api_admin_user_save_nix_options';
import { ApiObjNixOption } from '../../../api/object/nix_option/ApiObjNixOption';

type Props = {
    baseData: ApiObjBaseData,
    userData: UserData,
    adminBaseData: ApiObjAdminBaseData,
    otherUserId: number,
}

export const AdminUserTabNixOptions: FunctionComponent <Props> = ({
    baseData,
    userData,
    adminBaseData,
    otherUserId
}) => {

    if (!userData.isLoggedInAndAdmin()) {
        return <ErrorPageNoAdmin />;
    }

    const [mapUserNixOptions, setMapUserNixOptions] = useState <Map<number, number>> (new Map());
    const [isLoading, setIsLoading] = useState <boolean> (true);
    const [isSaving, setIsSaving] = useState <boolean> (false);
    const isActive = !isLoading && !isSaving;

    // ======================================================================
    // === Setup
    // ======================================================================

    useEffect(() => {

        setIsLoading(true);
        api_admin_user_get_nix_options(otherUserId)
            .then((result) => {
                const newMap = new Map();
                for (const item of result) {
                    newMap.set(item, item);
                }
                setMapUserNixOptions(newMap);
                setIsLoading(false);
            });

    }, [otherUserId]);

    // ======================================================================
    // === Actions
    // ======================================================================
  
    const actionSaveNixOptions = () => {

        setIsSaving(true);

        const nixOptionIds : Array<number> = [];
        mapUserNixOptions.forEach((value: number, key: number) => {
            nixOptionIds.push(key);
        });

        api_admin_user_save_nix_options(otherUserId, nixOptionIds)
            .then((result) => {
                const newMap = new Map();
                for (const item of result) {
                    newMap.set(item, item);
                }
                setMapUserNixOptions(newMap);
                setIsSaving(false);

            }).catch((err) => {
                console.error("Could not save nix options");
                console.error(err);
                // Skip to set isSaving to fails to make error obvious.
            });
    }

    const actionOnCheckboxChange = (nixOptionId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const newMap = new Map(mapUserNixOptions);
        if (isChecked) {
            newMap.set(nixOptionId, nixOptionId);
        } else {
            newMap.delete(nixOptionId);
        }
        setMapUserNixOptions(newMap);
    }
    
    // ======================================================================
    // === Render
    // ======================================================================

    const renderList = () : JSX.Element => {

        const elements : Array<JSX.Element> = [];
        for (const item of adminBaseData.nix_options) {
            if (item.is_premium) {
                elements.push(renderItem(item));
            }
        }

        return (
            <Box>
                {elements}
            </Box>
        );
    }

    const renderItem = (nixOption: ApiObjNixOption) : JSX.Element => {

        const isSelected = mapUserNixOptions.has(nixOption.id);

        return (
            <Box
                key={nixOption.id}
                display={'flex'}
                flexDirection={'row'}
                alignContent={'center'}>
                    <Box>
                        <Checkbox
                            checked={isSelected}
                            disabled={!isActive}
                            color={'secondary'}
                            onClick={(event) => { event.stopPropagation(); }}
                            onChange={(event) => { actionOnCheckboxChange(nixOption.id, event) }}/>
                    </Box>
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        {nixOption.name}
                    </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (<Box padding={'14px'}>Laddar...</Box>);
    }

    return (
        <Box padding={'14px'} display={'flex'} flexDirection={'column'}>
            {renderList()}
            <Box paddingTop={'15px'}>
                <Button variant='contained' color='secondary' onClick={actionSaveNixOptions} disabled={!isActive}>
                    Spara
                </Button>
            </Box>
        </Box>
    );
}
